import {useQuery} from "@tanstack/react-query";
import {useIntl} from "react-intl";
import {taxiOverview} from "@/api/apiClient";
import {
    handlePublicHashError,
    isNonRetryableError,
    usePublicOrderHash,
} from "@/features/configuration";
import type {LocationSuggestionWithCoordinates} from "@/features/location-suggestions";
import {taxiStopsPayload} from "../utils/transformers";

type UseTaxiOverviewProps = {
    pickupLocation: LocationSuggestionWithCoordinates | undefined;
    dropoffLocation: LocationSuggestionWithCoordinates | undefined;
    isViableRoute: boolean;
};

export function useTaxiOverview({
    pickupLocation,
    dropoffLocation,
    isViableRoute,
}: UseTaxiOverviewProps) {
    const {orderHash, clearOrderHash} = usePublicOrderHash();
    const intl = useIntl();

    return useQuery({
        queryKey: ["taxiOverview", pickupLocation?.location, dropoffLocation?.location, orderHash],
        queryFn: async () => {
            const hash = orderHash ?? undefined;
            try {
                const response = await taxiOverview({
                    tierGroup: "TAXI",
                    stops: taxiStopsPayload(
                        [
                            {
                                name: "",
                                phoneNumber: "",
                                location: pickupLocation,
                                noteForDriver: "",
                            },
                        ],
                        [dropoffLocation]
                    ),
                    orderType: "NORMAL",
                    publicHash: hash,
                });
                return response;
            } catch (error) {
                handlePublicHashError(error, intl, clearOrderHash);
                throw error;
            }
        },
        enabled: !!pickupLocation && !!dropoffLocation && isViableRoute,
        retry: (failureCount, error) => {
            if (isNonRetryableError(error)) return false;
            return failureCount < 3;
        },
        refetchOnWindowFocus: true,
        refetchInterval: 5 * 60 * 1000, // 5 minutes
    });
}
